import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { receiveKeys } from './keys';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeGetReturnDisposalInboundAreaResponse, TypeReturnDisposalInboundAreaApi } from '~/types/receive.types';

/**
 * [입고관리 > 회송/폐기 관리] 회수 처리 입고지(회수지) 주소 조회 API
 * @note 배열 파라미터는 반복 키(`inspectionIds=1&inspectionIds=2`)로 직렬화한다.
 *       혼합선택 제한으로 선택 카드가 모두 동일 입고지 → 단일 객체 응답.
 */
const fetchReturnDisposalInboundArea = async (inspectionIds: number[]): Promise<TypeReturnDisposalInboundAreaApi> => {
  const { data } = await axiosInstance.get<TypeGetReturnDisposalInboundAreaResponse>(
    API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_INBOUND_AREA,
    { params: { inspectionIds }, paramsSerializer: { indexes: null } }
  );

  return data.data;
};

type TypeUseGetReturnDisposalInboundAreaQueryParams = {
  inspectionIds: number[];
  enabled?: boolean;
};

/** [입고관리 > 회송/폐기 관리] 회수 처리 입고지 주소 조회 Query Hook (모달 오픈 시에만 enabled) */
export const useGetReturnDisposalInboundAreaQuery = ({
  inspectionIds,
  enabled = true,
}: TypeUseGetReturnDisposalInboundAreaQueryParams) => {
  return useQuery({
    queryKey: receiveKeys.returnDisposalInboundArea(inspectionIds),
    queryFn: () => fetchReturnDisposalInboundArea(inspectionIds),
    enabled: enabled && inspectionIds.length > 0,
    staleTime: 0,
    gcTime: 1000 * 30,
  });
};

/** 회수지 주소 mock — 신청 팝업(inbound-area)과 상세 조회가 같은 값을 노출해야 해서 상수로 공유한다 */
export const MOCK_RETURN_DISPOSAL_INBOUND_ADDRESS = '경기도 김포시 아라육로 75 켄달스퀘어 김포 LP 4층 A405~407';

/** [입고관리 > 회송/폐기 관리] 회수 처리 입고지 주소 조회 Mock (파라미터 무시·시나리오 고정 응답) */
registerMockAPI<TypeGetReturnDisposalInboundAreaResponse | TypeErrorResponseData>({
  method: 'get',
  endpoint: API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_INBOUND_AREA,
  label: '회송폐기관리/회수 처리 입고지 주소 조회',
  scenarios: [
    {
      name: '정상 응답',
      response: {
        data: {
          id: 147,
          warehouse: 'GGH1',
          code: 'GGH1_SVST4',
          name: '김포상온_노은석_테스트4',
          address: MOCK_RETURN_DISPOSAL_INBOUND_ADDRESS,
        },
      },
    },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '입고지 주소를 불러오지 못했습니다.' } },
      status: 500,
    },
  ],
});
